import React from "react";
import { Alert, Box, Skeleton } from "@mui/material";
import FormItem from "@components/FormItem/FormItem";
import { useBayHireMyCompanyDetails } from "@features/bay-hire/hooks/useBayHireQueries";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

/**
 * DFT-0044 slice F (FE-01/FE-02, Q13): `03_Bay Hire License.png`'s six read-only, prefilled company
 * fields, at the top of the create form, inside "Required information".
 *
 * <p>🔴 An earlier version of this component reused `CompanyDetailsCard` (the request detail page's
 * label-above-value card) wholesale. That was wrong for two reasons, both found only by checking
 * this wireframe directly rather than assuming the detail page's Figma p10 layout applied here too:
 *
 * <ol>
 *   <li>Different field GROUPING. This wireframe pairs P.O. Box with Emirate and Telephone with
 *       Email, with company name and FAX each alone on their own row. `CompanyDetailsCard` pairs
 *       company name with Telephone and P.O. Box with Email instead - a different, unrelated Figma
 *       source (p10), reused here by assumption rather than by checking.</li>
 *   <li>Different VISUAL TREATMENT. This wireframe renders every field as a bordered input box with
 *       a label above it - visually identical to every editable field elsewhere on this same form,
 *       just disabled. `CompanyDetailsCard` renders plain label/value text pairs with no border at
 *       all, which is the right look for a value already committed to a request on the detail page,
 *       but not for a form the vendor is still filling in.</li>
 * </ol>
 *
 * <p>DFT-0111 (was: a hand-rolled `CustomFormLabel` + `Input` pair): re-checked against how other
 * mature modules render a read-only/prefilled field inside an otherwise-editable form (vessel-call's
 * `MovementVesselDetails.tsx`, `Port.tsx`, etc.) - every one of them uses `FormItem` itself, just
 * with `disabled` set, even for fields the domain treats as effectively fixed. The hand-rolled
 * version existed only because these six fields have no place in `BayHireRequestFormData`/
 * `BayHireRequestCreate` at all (the domain already knows the caller's company from their own
 * token - there is nothing to submit), so they can't be registered on the SAME react-hook-form
 * instance as the rest of the form the way `MovementVesselDetails.tsx`'s disabled fields are. That
 * reason still holds, but it only rules out sharing the MAIN form's RHF instance - it does not rule
 * out `FormItem` itself. This now uses a second, local `useForm()` purely so `FormItem` has the
 * `useFormContext()` it requires; this local form is never rendered inside a `<form>`, has no submit
 * handler, and its values are never read outside this component - it exists solely to reuse
 * `FormItem`'s rendering, not to participate in the real submission. This gets exact, guaranteed
 * visual parity (spacing, label/input/helper-text layout) with every other field on the form for
 * free, instead of a parallel implementation that has to be manually kept in sync by hand (the
 * source of a whole round of spacing bugs during DFT-0111 - see plan.md for the full history).
 *
 * <p>One real behavioral difference from a normal `FormItem` usage remains, and needs the one small
 * override below: `FormItem`'s single `disabled` prop drives both the input's disabled state AND
 * the label's greyed-out styling together, but these six fields need to stay disabled
 * UNCONDITIONALLY (they're never editable) while their labels should only grey out when the whole
 * FORM is disabled (e.g. submitting) - matching every editable field's label on the same form,
 * which stays normal-colored while the form itself is enabled. See the regression-guard test in
 * `BayHireRequestForm.test.tsx` ("these labels used to hard-code disabled..."). Solved with a
 * narrow, Bay-Hire-scoped `sx` override undoing the label's disabled color specifically when the
 * form itself is NOT disabled - the same ancestor-`sx`-override-a-child-MUI-component technique
 * `BayHireRequestDetail.tsx` already uses for `MuiTab-root`, just for a label color instead of
 * layout.
 *
 * <p>Rendered as siblings of the RHF-registered fields inside the SAME `FormSection` grid in
 * `BayHireRequestForm` - `FormSection` only cares about DOM order for its grid placement, not form
 * state, so disabled and registered fields can share one grid without friction.
 */
interface CompanyDisplayFormData {
  companyName: string;
  poBox: string;
  emirate: string;
  telephoneNo: string;
  emailAddress: string;
  fax: string;
}

interface ReadOnlyFieldProps {
  name: keyof CompanyDisplayFormData;
  label: string;
  testId: string;
  fullWidth?: boolean;
  /** Tracks the FORM's own disabled state (e.g. while submitting) - NOT this field's own
   * permanent immutability, which lives on the input alone. See the component doc for why. */
  formDisabled?: boolean;
}

const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({
  name,
  label,
  testId,
  fullWidth,
  formDisabled,
}) => (
  <Box
    className={fullWidth ? "md:col-span-2" : undefined}
    sx={
      // See the class doc above: FormItem's `disabled` prop greys out the label too, but these
      // fields' labels should only look disabled when the whole form is - undo the greyed color
      // in every other case, without touching FormItem.tsx's own disabled-label styling.
      !formDisabled
        ? { "& .MuiFormLabel-root.Mui-disabled": { color: (theme) => theme.palette.text.primary } }
        : undefined
    }
  >
    <FormItem<CompanyDisplayFormData, typeof name>
      name={name}
      type="text"
      label={label}
      testId={testId}
      disabled
    />
  </Box>
);

interface BayHireCompanyDetailsSectionProps {
  disabled?: boolean;
}

const BayHireCompanyDetailsSection: React.FC<BayHireCompanyDetailsSectionProps> = ({
  disabled,
}) => {
  const { t } = useTranslation();
  const { data: company, isLoading, isError } = useBayHireMyCompanyDetails();

  // Local, display-only form - see the class doc above for why this exists instead of registering
  // these fields on the main form. `values` (not `defaultValues`) keeps this in sync whenever the
  // query resolves/refetches, since the company details load asynchronously after first render.
  const displayForm = useForm<CompanyDisplayFormData>({
    values: {
      companyName: company?.companyNameEnglish ?? "",
      poBox: company?.poBox ?? "",
      emirate: company?.emirates ?? "",
      telephoneNo: company?.proOfficeNumber ?? "",
      emailAddress: company?.proEmail ?? "",
      fax: company?.fax ?? "",
    },
  });

  if (isLoading) {
    return (
      <>
        <div className="md:col-span-2" data-testid="bay-hire-form-company-details-loading">
          <Skeleton variant="rounded" height={56} />
        </div>
        <Skeleton variant="rounded" height={56} />
        <Skeleton variant="rounded" height={56} />
      </>
    );
  }

  if (isError || !company) {
    // Best-effort, matching the BFF's own semantics (a lookup failure returns nulls, not a 500) -
    // an unreachable Reference Data must not block the vendor from submitting a request at all.
    return (
      <div className="md:col-span-2">
        <Alert severity="warning" data-testid="bay-hire-form-company-details-error">
          {t("bay-hire.form.company-details-unavailable")}
        </Alert>
      </div>
    );
  }

  return (
    <FormProvider {...displayForm}>
      <ReadOnlyField
        fullWidth
        formDisabled={disabled}
        name="companyName"
        label={t("bay-hire.form.portOperatorCompanyName")}
        testId="bay-hire-form-company-name"
      />
      <ReadOnlyField
        name="poBox"
        label={t("bay-hire.form.poBox")}
        testId="bay-hire-form-company-po-box"
        formDisabled={disabled}
      />
      <ReadOnlyField
        name="emirate"
        label={t("bay-hire.form.emirate")}
        testId="bay-hire-form-company-emirate"
        formDisabled={disabled}
      />
      <ReadOnlyField
        name="telephoneNo"
        label={t("bay-hire.form.telephoneNo")}
        testId="bay-hire-form-company-telephone"
        formDisabled={disabled}
      />
      <ReadOnlyField
        name="emailAddress"
        label={t("bay-hire.form.emailAddress")}
        testId="bay-hire-form-company-email"
        formDisabled={disabled}
      />
      <ReadOnlyField
        name="fax"
        label={t("bay-hire.form.fax")}
        testId="bay-hire-form-company-fax"
        formDisabled={disabled}
      />
      {/* Grid filler, not decoration. Without it, CSS Grid's default sparse auto-placement pulls
          the NEXT field ("Name of Port company responsible") into FAX's empty second column
          instead of starting the fresh row it needs to be paired with Designation on -
          `grid-auto-flow` only skips forward to a new row automatically when an item does not FIT
          in the remaining space (as the wider fields below rely on); it does not do so for a
          same-size item that fits perfectly into a gap. */}
      <div className="hidden md:block" aria-hidden="true" />
    </FormProvider>
  );
};

export default BayHireCompanyDetailsSection;
